import { useCallback, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * useConfirmDialog — replaces `window.confirm()` with a branded shadcn AlertDialog.
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirmDialog();
 *   ...
 *   const ok = await confirm({ title: "Delete inquiry?", description: "This cannot be undone." });
 *   if (!ok) return;
 *
 * Render <ConfirmDialog /> anywhere inside the page (once).
 */
export default function useConfirmDialog() {
  const [state, setState] = useState({ open: false });

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "This action cannot be undone.",
        confirmLabel: opts.confirmLabel || "Delete",
        cancelLabel: opts.cancelLabel || "Cancel",
        destructive: opts.destructive !== false, // defaults to destructive styling
        resolve,
      });
    });
  }, []);

  const onClose = (value) => {
    state.resolve?.(value);
    setState({ open: false });
  };

  const ConfirmDialog = () => (
    <AlertDialog open={!!state.open} onOpenChange={(v) => { if (!v) onClose(false); }}>
      <AlertDialogContent
        data-testid="confirm-dialog"
        className="bg-[#0a0a0a] border-white/10 text-zinc-100"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display font-light text-2xl tracking-tight">
            {state.title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400 text-sm leading-relaxed">
            {state.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="confirm-cancel"
            onClick={() => onClose(false)}
            className="bg-transparent border-white/10 text-zinc-300 hover:bg-white/5 hover:text-zinc-100"
          >
            {state.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-ok"
            onClick={() => onClose(true)}
            className={
              state.destructive
                ? "bg-red-500/90 hover:bg-red-500 text-white"
                : "bg-[#C5A059] hover:bg-[#D4AF37] text-black"
            }
          >
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, ConfirmDialog };
}
